import {
  ResponsiveModal,
  ResponsiveModalContent,
} from "@/components/ui/responsive-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExternalLink, Copy, X as XClose, Phone, MessageCircle, Mail, MessageSquare, ChevronDown, Check, PhoneCall, Siren, LockKeyhole, Loader2, Unlock } from "lucide-react";
import { DEMO_OWNER_INFO_MESSAGE, DEMO_OWNER_PRODUCTS_MESSAGE, DEMO_LOCKED_PRODUCTS_MESSAGE, DEMO_PROFILE_ID } from "@/utils/demoProfile";
import { Checkbox } from "@/components/ui/checkbox";
import { WhatsAppIcon, TelegramIcon, XIcon } from "../ui/SocialIcons";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useConversation } from "@/hooks/useConversation";
import { useAuth } from "@/hooks/useAuth";
import { PasswordRequirement } from "@/components/find-owner/PasswordPromptDialog";
import { optimizeCloudinaryImage } from "@/utils/imageOptimizer";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  x_username: string | null;
  bio: string | null;
  heading?: string | null;
  site_link?: string | null;
  mask_bio?: boolean;
  metro_card_number: string | null;
  vehicle_number: string | null;
  created_at: string;
  updated_at: string;
  show_email: boolean;
  show_mobile: boolean;
  metefy_token: string | null;
  photo_url?: string | null;
  // NEW
  social_sms?: string | null;
  social_call?: string | null;
  social_telegram?: string | null;
  social_whatsapp?: string | null;
  use_social_icons?: boolean;
  show_only_filled_socials?: boolean;
}

interface AboutOwnerProfileProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  profile: Profile | null;
  copyTweetTemplate: () => void;
  // Show demo-only controls (e.g., mask bio toggle)
  isDemo?: boolean;
  passwordRequirement?: PasswordRequirement | null;
  passwordValue?: string;
  passwordError?: string;
  isPasswordValidating?: boolean;
  onPasswordChange?: (value: string) => void;
  onPasswordSubmit?: () => void;
  onPasswordCancel?: () => void;
}

// Add a util function to clean telegram input
function normalizeTelegram(value?: string | null): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (/^\d{7,}$/.test(trimmed)) {
    // numeric, add +91
    return `+91${trimmed.replace(/^0+/, '')}`;
  }
  if (trimmed.startsWith('+')) return trimmed;
  if (trimmed.startsWith('@')) return trimmed;
  // Otherwise treat as username (add @)
  return `@${trimmed}`;
}

function escapeHtml(value: string) {
  const escapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return value.replace(/[&<>"']/g, (char) => escapeMap[char]);
}

function sanitizeSiteLinkContent(raw?: string | null) {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (typeof DOMParser === 'undefined') {
    return escapeHtml(trimmed);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${trimmed}</div>`, 'text/html');
  const container = doc.body.firstElementChild as HTMLElement | null;
  if (!container) return escapeHtml(trimmed);

  container.querySelectorAll('*').forEach((el) => {
    if (el.tagName.toLowerCase() !== 'a') {
      const textNode = doc.createTextNode(el.textContent || '');
      el.replaceWith(textNode);
      return;
    }

    const href = el.getAttribute('href') || '';
    if (!/^https?:\/\//i.test(href)) {
      el.removeAttribute('href');
    }
    el.removeAttribute('style');
    el.setAttribute('rel', 'noopener noreferrer');
    el.setAttribute('target', '_blank');
    el.setAttribute(
      'class',
      'text-primary font-medium hover:text-primary/80 hover:underline'
    );
  });

  return container.innerHTML;
}

function extractPlainText(raw?: string | null) {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  return trimmed.replace(/<[^>]*>/g, '').trim() || null;
}

export function AboutOwnerProfile({
  isOpen,
  setIsOpen,
  profile,
  copyTweetTemplate,
  isDemo = false,
  passwordRequirement = null,
  passwordValue = "",
  passwordError,
  isPasswordValidating = false,
  onPasswordChange,
  onPasswordSubmit,
  onPasswordCancel,
}: AboutOwnerProfileProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { createOrGetConversation, isLoading: isCreatingConversation } = useConversation();
  
  // Array of background images to randomly select from - optimized for performance
  const backgroundImages = useMemo(() => [
    optimizeCloudinaryImage("https://res.cloudinary.com/deqzsmrxq/image/upload/v1760997986/Untitled_design_21_qq7mzf.png", 200, 70, 'auto'),
    optimizeCloudinaryImage("https://res.cloudinary.com/deqzsmrxq/image/upload/v1761001501/Untitled_design_23_aukr7e.png", 200, 70, 'auto'),
  ], []);
  
  const [selectedImage, setSelectedImage] = useState(backgroundImages[0]);
  const [showXDropdown, setShowXDropdown] = useState(false);
  const [unlockDigits, setUnlockDigits] = useState<string[]>(["", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(4).fill(null));
  const [demoMaskEnabled, setDemoMaskEnabled] = useState(true);
  const [registeredProductNames, setRegisteredProductNames] = useState<string[]>([]);
  const isPasswordProtected = Boolean(passwordRequirement);
  const isDemoPrivacyLocked = isDemo && !demoMaskEnabled;
  const shouldShowProtectedCard = isPasswordProtected || isDemoPrivacyLocked;
  const isContactRestricted = shouldShowProtectedCard;
  const passwordInstructions = useMemo(() => {
    if (!passwordRequirement) return null;
    const isVehicle = passwordRequirement.type === "vehicle";
    const isLastFourMode = passwordRequirement.mode === "last4";
    const instructions = isLastFourMode
      ? `Enter the last 4 characters of the ${isVehicle ? "vehicle number" : "metro card number"}.`
      : "Enter the 4 digits written on the QR sticker.";
    const helper = isLastFourMode
      ? `Example: if the ${isVehicle ? "vehicle number" : "metro card number"} is ABCD1234, type 1234.`
      : "The sticker has a 4-digit code inside the frame.";
    return { instructions, helper };
  }, [passwordRequirement]);

  // Wrapper function for closing drawer - matches SubmitFoundItemDrawer pattern
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  // Add history state when drawer opens, handle back button
  useEffect(() => {
    if (isOpen) {
      // Push a history state so back button can close the drawer
      window.history.pushState({ ownerProfileOpen: true }, '');
      
      const onPopState = () => {
        if (isOpen) {
          // Call handleClose - ResponsiveModal/VaulDrawer will handle the animation
          handleClose();
        }
      };
      
      window.addEventListener('popstate', onPopState);
      return () => {
        window.removeEventListener('popstate', onPopState);
      };
    }
  }, [isOpen, handleClose]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showXDropdown) {
        const target = event.target as Element;
        if (!target.closest('.x-dropdown-container')) {
          setShowXDropdown(false);
        }
      }
    };

    if (showXDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showXDropdown]);

  // Randomly select an image when the dialog opens
  useEffect(() => {
    if (isOpen) {
      const randomIndex = Math.floor(Math.random() * backgroundImages.length);
      setSelectedImage(backgroundImages[randomIndex]);
    }
  }, [isOpen]);

  // Load registered product names for this profile (owner). If RLS blocks, ignore.
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Reset products when profile changes or dialog opens
        setRegisteredProductNames([]);
        
        if (!profile?.id || !isOpen) return;
        // Skip fetching when profile.id is not a UUID (e.g., demo ids) to avoid 400 from PostgREST
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(profile.id)) return;
        
        const { data, error } = await supabase
          .from('products')
          .select('name')
          .eq('user_id', profile.id);
        if (error) {
          console.error('Error fetching products:', error);
          return; // silently ignore if not permitted
        }
        if (data && data.length > 0) {
          const productNames = data.map((d: any) => d.name).filter(Boolean);
          setRegisteredProductNames(productNames);
        }
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };
    loadProducts();
  }, [profile?.id, isOpen]);

  const handleMessageClick = async () => {
    if (!profile?.id) return;
    
    if (!user) {
      // Store the profile info in sessionStorage to restore after auth
      sessionStorage.setItem('pendingMessage', JSON.stringify({
        userId: profile.id,
        name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User',
        photo: profile.photo_url || '/default-avatar.png'
      }));
      navigate('/auth', { state: { from: '/messaging' } });
      setIsOpen(false);
      return;
    }
    
    try {
      const result = await createOrGetConversation({
        id: profile.id,
        name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User',
        photo: profile.photo_url || '/default-avatar.png'
      });
      
      if (result) {
        setIsOpen(false); // Close dialog first
        
        // Navigate and ensure event is dispatched after navigation is complete
        navigate('/messaging', {
          state: {
            openConversation: {
              contact: result.contact,
              conversationId: result.conversationId
            }
          }
        });
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast({
        title: "Error",
        description: "Failed to open conversation. Please try again.",
        variant: "destructive"
      });
    }
  };

  const isLoadingProfile = !profile;

  // 2. Before rendering social/contact icons, decide the values to use:
  const DEFAULT_SMS = "+918307562996";
  const DEFAULT_CALL = "+918307562996";
  const DEFAULT_TELEGRAM = "+918307562996"; // could be username, keep as needed
  const DEFAULT_WHATSAPP = "+918307562996";
  const useUserSocials = profile?.use_social_icons && !shouldShowProtectedCard;
  // Explicitly check for show_only_filled_socials - default to false if not present
  const showOnlyFilled = !!(profile?.show_only_filled_socials) && useUserSocials;
  const smsValue = useUserSocials && profile?.social_sms ? profile.social_sms : DEFAULT_SMS;
  const callValue = useUserSocials && profile?.social_call ? profile.social_call : DEFAULT_CALL;
  const rawTelegram = useUserSocials && profile?.social_telegram ? profile.social_telegram : DEFAULT_TELEGRAM;
  const telegramValue = normalizeTelegram(rawTelegram);
  const whatsappValue = useUserSocials && profile?.social_whatsapp ? profile.social_whatsapp : DEFAULT_WHATSAPP;
  
  // Determine which icons to show based on show_only_filled_socials setting
  // During loading, show all icons (skeleton state)
  // After loading, respect the show_only_filled_socials setting
  // Helper function to check if a field has a non-empty value
  const hasValue = (value: string | null | undefined): boolean => {
    return !!(value && typeof value === 'string' && value.trim() !== '');
  };
  
  const showSmsIcon = isLoadingProfile || !showOnlyFilled || (showOnlyFilled && hasValue(profile?.social_sms));
  const showCallIcon = isLoadingProfile || !showOnlyFilled || (showOnlyFilled && hasValue(profile?.social_call));
  const showTelegramIcon = isLoadingProfile || !showOnlyFilled || (showOnlyFilled && hasValue(profile?.social_telegram));
  const showWhatsappIcon = isLoadingProfile || !showOnlyFilled || (showOnlyFilled && hasValue(profile?.social_whatsapp));
  const showXIcon = isLoadingProfile || hasValue(profile?.x_username);

  // Normalize phone-like inputs to include +91 when country code is missing
  function normalizePhone(value?: string | null): string {
    if (!value) return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('+')) return trimmed;
    const digits = trimmed.replace(/\D/g, '').replace(/^0+/, '');
    if (!digits) return '';
    return `+91${digits}`;
  }

  const normalizedSms = normalizePhone(smsValue);
  const normalizedCall = normalizePhone(callValue);
  const normalizedWhatsapp = normalizePhone(whatsappValue);
  const fallbackContactNumber = "+918307562996";
  const fallbackNormalized = normalizePhone(fallbackContactNumber);
  const headingText = useMemo(
    () => extractPlainText(profile?.heading),
    [profile?.heading]
  );
  const sanitizedSiteLink = useMemo(
    () => (profile?.site_link ? sanitizeSiteLinkContent(profile.site_link) : null),
    [profile?.site_link]
  );

  return (
    <ResponsiveModal open={isOpen} setOpen={handleClose} direction="bottom" classname="max-w-md">
      <ResponsiveModalContent
        className="tweet-dialog-capture main-dialog-container bg-card text-foreground border-none w-full sm:max-w-md p-0 pb-6 focus:outline-none focus-visible:outline-none"
        titleId="owner-profile-title"
      >
        <div className="space-y-3 relative p-3 sm:p-6 bg-card pb-6">
          {/* Header */}
          <div className="text-center mb-4">
            <h2 id="owner-profile-title" className="text-2xl font-normal text-foreground w-full flex items-center justify-center gap-2">
              {!headingText && (
                <div 
                  className="w-6 h-6"
                  style={{
                    maskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                    maskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                    backgroundColor: 'hsl(var(--primary))',
                    WebkitMaskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                    WebkitMaskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                  }}
                  role="img"
                  aria-label="Metefy Logo"
                />
              )}
              {headingText || "Owner Found at Metefy"}
            </h2>
            {sanitizedSiteLink && (
              <p
                className="text-sm text-center px-4 mt-2 text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: sanitizedSiteLink }}
              />
            )}
          </div>
            <div className="flex items-center space-x-4 bg-card p-4 rounded-lg">
              <Avatar className="h-16 w-16 border-2 border-primary">
                {!isLoadingProfile ? (
                  <>
                    <AvatarImage src={profile.photo_url || '/default-avatar.png'} alt="User Avatar" />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                      {profile.first_name?.[0]}{profile.last_name?.[0]}
                    </AvatarFallback>
                  </>
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-muted-foreground">...</div>
                )}
              </Avatar>
              <div>
                <h3 className="text-xl font-semibold text-foreground">
                  {isLoadingProfile ? 'Loading...' : (<>{profile.first_name} {profile.last_name}</>)}
                </h3>
                {!isLoadingProfile && profile.x_username && (
                  <div className="mt-1 flex items-center gap-1 relative">
                    <XIcon className="h-4 w-4 text-primary" />
                    <div className="relative x-dropdown-container">
                      <button
                        onClick={() => setShowXDropdown(!showXDropdown)}
                        className="text-primary font-normal text-sm flex items-center gap-1 hover:text-primary/80 transition-colors"
                        style={{ fontWeight: 400 }}
                      >
                        @{profile.x_username}
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      
                      {showXDropdown && (
                        <div className="absolute top-6 left-0 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[160px]">
                          <button
                            onClick={() => {
                              window.open(`https://x.com/${profile.x_username}`, '_blank');
                              setShowXDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Open Profile
                          </button>
                          <button
                            onClick={() => {
                              const ownerUsername = profile.x_username || "username_not_available";
                              const identifiers: string[] = [];
                              if (profile.metro_card_number) identifiers.push(`metro card number ${profile.metro_card_number}`);
                              if (profile.vehicle_number) identifiers.push(`vehicle number ${profile.vehicle_number}`);
                              if (profile.metefy_token) identifiers.push(`metefy token ${profile.metefy_token.replace(/(\d{4})(?=\d)/g, '$1-')}`);
                              const identifiersText = identifiers.length > 0 ? identifiers.join(', ') : "N/A";
                              const tweetText = `Hi @mete_fy, I found a lost item belonging to @${ownerUsername} via metefy.com, with ${identifiersText}. Please help me connect with the owner to return it safely.`;
                              const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
                              window.open(tweetUrl, '_blank');
                              setShowXDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                          >
                            <XIcon className="h-3 w-3" />
                            Make a Tweet
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
              </div>
            </div>
            {/* Owner details section with optional masking (shows skeleton in loading state) */}
            {shouldShowProtectedCard ? (
              <div className="bg-card p-4 rounded-xl space-y-4">
                <div className="flex items-center gap-2">
                  <LockKeyhole className="h-5 w-5 text-primary" />
                  <div>
                    <h4 className="font-semibold text-foreground">This profile is protected</h4>
                    <p className="text-sm text-foreground">
                      {isPasswordProtected
                        ? passwordInstructions?.instructions
                        : "Enter the 4 digits written on the QR sticker."}
                    </p>
                  </div>
                </div>
                {isPasswordProtected ? (
                  <>
                    <Input
                      value={passwordValue}
                      onChange={(e) => onPasswordChange?.(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase())}
                      maxLength={4}
                      placeholder="____"
                      className="uppercase tracking-[0.4em] text-center text-lg h-12 bg-background border border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      {passwordInstructions?.helper || "Only letters and numbers are accepted."}
                    </p>
                    {passwordError && <p className="text-sm text-red-600 text-center">{passwordError}</p>}
                    <Button 
                      className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground" 
                      onClick={onPasswordSubmit}
                      disabled={isPasswordValidating}
                    >
                      {isPasswordProtected && isPasswordValidating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Validating...
                        </>
                      ) : (
                        "Unlock profile"
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Input
                      value={unlockDigits.join('')}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 4);
                        const padded = val.split('').concat(Array(4).fill('')).slice(0, 4);
                        setUnlockDigits(padded);
                      }}
                      maxLength={4}
                      placeholder="____"
                      className="uppercase tracking-[0.4em] text-center text-lg h-12 bg-background border border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      For demo: 1001 is written on sticker.
                    </p>
                    <Button
                      className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground"
                      onClick={() => {
                        const token = (profile?.metefy_token || "").replace(/[^\d]/g, "");
                        if (token.length === 12) {
                          const middle = token.substring(4, 8);
                          if (unlockDigits.join("") === middle) {
                            setDemoMaskEnabled(true);
                            setUnlockDigits(["", "", "", ""]);
                          }
                        }
                      }}
                    >
                      Unlock profile
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <>
            <div className="bg-card p-4 rounded-lg">
              <div className="mb-2 flex items-center justify-between gap-3">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  Owner Info
                </h4>
                {isDemo && (
                  <button
                    onClick={() => {
                      setDemoMaskEnabled((prev) => {
                        const next = !prev;
                        setUnlockDigits(["", "", "", ""]);
                        return next;
                      });
                    }}
                    className="flex items-center gap-2 rounded-full border border-primary/30 px-3 py-1 text-xs font-medium text-foreground hover:border-primary hover:bg-primary/10 transition"
                    aria-label="Toggle demo privacy lock"
                  >
                    <>
                      {demoMaskEnabled ? (
                        <Unlock className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <LockKeyhole className="h-3.5 w-3.5 text-primary" />
                      )}
                      <span>Try privacy mode</span>
                    </>
                  </button>
                )}
              </div>
              {isLoadingProfile ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-3 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              ) : isDemo && !demoMaskEnabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <LockKeyhole className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-semibold text-foreground">This profile is protected</h4>
                      <p className="text-sm text-foreground">
                        Enter the 4 digits written on the QR sticker.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Input
                      value={unlockDigits.join('')}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 4);
                        const padded = val.split('').concat(Array(4).fill('')).slice(0, 4);
                        setUnlockDigits(padded);
                      }}
                      maxLength={4}
                      placeholder="____"
                      className="uppercase tracking-[0.4em] text-center text-lg h-12 bg-background border border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    The sticker has a 4-digit code inside the frame.
                  </p>
                  <Button
                    className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground"
                    onClick={() => {
                      const token = (profile.metefy_token || "").replace(/[^\d]/g, "");
                      if (token.length === 12) {
                        const middle = token.substring(4, 8);
                        if (unlockDigits.join("") === middle) {
                          setDemoMaskEnabled(true);
                          setUnlockDigits(["", "", "", ""]);
                        }
                      }
                    }}
                  >
                    Unlock profile
                  </Button>
                </div>
              ) : profile?.id === DEMO_PROFILE_ID ? (
                <p className="text-sm text-foreground whitespace-pre-line">
                  {DEMO_OWNER_INFO_MESSAGE}
                </p>
              ) : isDemo ? (
                <div className="text-sm text-foreground">
                  <p>1. This is a demo profile.</p>
                  <p>
                    2. <a href="https://metefy.in" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Buy QR stickers</a>.
                  </p>
                  <p>
                    3. <span onClick={() => { setIsOpen(false); navigate('/profile'); }} className="text-primary hover:underline cursor-pointer">Activate the QR</span> by creating a profile.
                  </p>
                  <p>4. Apply stickers on products.</p>
                </div>
              ) : (
                profile.bio ? (
                  <p className="text-sm text-foreground whitespace-pre-line">{profile.bio}</p>
                ) : null
              )}
            </div>
            <div className="bg-card p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground">Owner Products</h4>
                {isDemoPrivacyLocked && (
                  <span className="text-xs text-muted-foreground">Locked</span>
                )}
              </div>
              {isLoadingProfile ? (
                <div className="text-sm text-foreground">
                  <span className="inline-block h-4 bg-muted rounded w-3/4 animate-pulse"></span>
                </div>
              ) : isDemoPrivacyLocked ? (
                <p className="text-sm text-foreground">{DEMO_LOCKED_PRODUCTS_MESSAGE}</p>
              ) : (
                <p className="text-sm text-foreground">
                  {(() => {
                    if (profile?.id === DEMO_PROFILE_ID) {
                      return DEMO_OWNER_PRODUCTS_MESSAGE;
                    }
                    const items: string[] = [];
                    if (profile.metro_card_number) items.push(`Metro Card (${profile.metro_card_number})`);
                    if (profile.vehicle_number) items.push(`Vehicle (${profile.vehicle_number})`);
                    if (profile.metefy_token) items.push('QR Stickers');
                    if (isDemo) {
                      items.push('Phone', 'Laptop', 'Charger', 'Wallet');
                    } else if (registeredProductNames.length) {
                      items.push(...registeredProductNames);
                    }
                    const unique = Array.from(new Set(items));
                    return unique.join(', ');
                  })()}
                </p>
              )}
            </div>
              </>
            )}
            
            <div className="bg-card p-4 rounded-lg">
              <div className="text-right">
                <span className="text-sm text-foreground">
                  {isContactRestricted ? "Report the found item to Metefy" : "Report the found item to Owner or Metefy"}
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-2">
              {showSmsIcon && (
                <button
                  onClick={() => {
                    if (isLoadingProfile) return;
                    const number = isContactRestricted ? fallbackNormalized : normalizedSms;
                    if (number) window.location.href = `tel:${number}`;
                  }}
                  className="p-2 bg-red-600 text-white rounded-full hover:bg-opacity-90 transition-colors"
                >
                  <Siren className="h-5 w-5" />
                </button>
              )}
              {showCallIcon && (
                <button
                  onClick={() => {
                    if (isLoadingProfile) return;
                    const number = isContactRestricted ? fallbackNormalized : normalizedCall;
                    if (number) window.location.href = `tel:${number}`;
                  }}
                  className="p-2 bg-green-600 text-white rounded-full hover:bg-opacity-90 transition-colors"
                >
                  <PhoneCall className="h-5 w-5" />
                </button>
              )}
              {showXIcon && (
                <button
                  onClick={() => {
                    if (isLoadingProfile || !profile || !profile.x_username) return;
                    const ownerUsername = profile.x_username || "username_not_available";
                    const identifiers: string[] = [];
                    if (profile.metro_card_number) identifiers.push(`metro card number ${profile.metro_card_number}`);
                    if (profile.vehicle_number) identifiers.push(`vehicle number ${profile.vehicle_number}`);
                    if (profile.metefy_token) identifiers.push(`metefy token ${profile.metefy_token.replace(/(\d{4})(?=\d)/g, '$1-')}`);
                    const identifiersText = identifiers.length > 0 ? identifiers.join(', ') : "N/A";
                    const tweetText = `Hi @mete_fy, I found a lost item belonging to @${ownerUsername} via metefy.com, with ${identifiersText}. Please help me connect with the owner to return it safely.`;
                    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
                    window.open(tweetUrl, '_blank');
                  }}
                  className="p-2 bg-black text-white rounded-full hover:bg-opacity-90 transition-colors"
                  disabled={isLoadingProfile}
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
              {showTelegramIcon && (
                <a
                  href={isLoadingProfile || !profile ? undefined : (() => {
                    const message = `Hey, I found a lost item of ${profile.first_name} ${profile.last_name} whose metefy token is ${profile.metefy_token?.replace(/(\d{4})(?=\d)/g, '$1-')} and x account is ${profile.x_username ? '@' + profile.x_username : 'not available'}`;
                    if (isContactRestricted) {
                      const fallbackPhone = fallbackNormalized.replace(/^\+/, '');
                      return `https://wa.me/${fallbackPhone}?text=${encodeURIComponent(message)}`;
                    }
                    if (telegramValue.startsWith('@')) {
                      return `https://t.me/${telegramValue.replace(/^@/, '')}`;
                    } else {
                      const phoneNumber = telegramValue.replace(/^\+/, '').replace(/[^\d]/g, '');
                      return `https://t.me/+${phoneNumber}?text=${encodeURIComponent(message)}`;
                    }
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-[#0088cc] text-white rounded-full hover:bg-opacity-90 transition-colors"
                >
                  <TelegramIcon className="h-5 w-5" />
                </a>
              )}
              {showWhatsappIcon && (
                <a
                  href={isLoadingProfile || !profile ? undefined : (() => {
                    const message = `Hey, I found a lost item of ${profile.first_name} ${profile.last_name} whose metefy token is ${profile.metefy_token?.replace(/(\d{4})(?=\d)/g, '$1-')} and x account is ${profile.x_username ? '@' + profile.x_username : 'not available'}`;
                    const phoneNumber = (isContactRestricted ? fallbackNormalized : normalizedWhatsapp).replace(/^\+/, '');
                    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-[#25D366] text-white rounded-full hover:bg-opacity-90 transition-colors"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                </a>
              )}
            </div>
            
          {/* Image positioned at bottom left of main dialog */}
          <div className="absolute bottom-0 left-4 z-20">
            <img 
              src={selectedImage} 
              alt="Background decoration" 
              className="h-16 w-auto object-contain opacity-100 sm:h-20" 
            />
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
